/**
 * How a child row draws its commands (Ben, 24 Oct: a draft quote line's five stacked buttons were too bulky). A row
 * with up to two commands, neither destructive, shows them as buttons. Any other row shows its main command
 * (`mainCommandOf`) as its one button and puts the rest behind one More… menu: the others that can run, then those
 * that cannot under "Not available now", each with its reason, as the record header folds its own. A destructive
 * command is the row's button only when its object declares it main (Ben, 24 Oct). The same on a phone and on a
 * desktop, so a person finds a command in the same place on both.
 *
 * The owning module may name the leading command, or none (`Command.main`, Ben's ruling 21): once it has spoken for a
 * list, only its `true` command leads, whatever `main:` declares, on a row and on the record header; with none
 * leading a row shows no button, only More….
 */
import type { Command } from "./contract";
import type { ManifestIndex } from "./manifest";

/** At most this many commands, none destructive, are drawn as buttons without a menu. */
export const ROW_BUTTONS_WITHOUT_MENU = 2;

export interface RowCommands {
  /** Drawn as buttons, in declared order; a disabled one says why beside it. */
  buttons: Command[];
  /** Behind More…: the other commands that can run, in declared order. */
  more: Command[];
  /** Behind More… under "Not available now": the commands that cannot run, each with its reason. */
  unavailable: Command[];
}

/**
 * The name of the command among these that `objectId` declares as its main one (`main: true`), if any. Only that
 * object's commands count: an artwork record's header also offers its current proof's commands, and the proof's main
 * one is not the artwork's.
 */
export function declaredMainOf(
  index: ManifestIndex,
  commands: readonly Command[],
  objectId: string,
): string | null {
  return (
    commands.find((c) => {
      const declared = index.commandOf(c.name);
      return declared.main && declared.target === objectId;
    })?.name ?? null
  );
}

/** The owner has said which command leads (or that none does) when any command in the list carries `main`. */
export function ownerHasSpoken(commands: readonly Command[]): boolean {
  return commands.some((c) => c.main !== undefined && c.main !== null);
}

/**
 * A row's one button: when the owner has spoken, its `main` command, else none; otherwise the declared main command
 * while it can run, destructive or not, else the first command that can run and is not destructive, else none (the
 * row shows only More…).
 */
export function mainCommandOf(
  commands: readonly Command[],
  declaredMain: string | null,
): Command | null {
  if (ownerHasSpoken(commands)) return commands.find((c) => c.main) ?? null;
  const declared = commands.find((c) => c.name === declaredMain);
  if (declared?.enabled) return declared;
  return commands.find((c) => c.enabled && !c.destructive) ?? null;
}

/**
 * The record header's primary command: when the owner has spoken, its `main` command, else none; otherwise the
 * declared main, else the first command. Only while it can run: a blocked command is never primary. Unlike a row's, it
 * may be destructive (Release…, Publish…): every header command that can run is a button anyway.
 */
export function headerMainOf(
  commands: readonly Command[],
  declaredMain: string | null,
): Command | null {
  const lead = headerLeadOf(commands, declaredMain);
  return lead?.enabled ? lead : null;
}

/** The command that leads the record header, whether or not it can run now. */
function headerLeadOf(
  commands: readonly Command[],
  declaredMain: string | null,
): Command | null {
  return ownerHasSpoken(commands)
    ? (commands.find((c) => c.main) ?? null)
    : (commands.find((c) => c.name === declaredMain) ?? commands[0] ?? null);
}

/**
 * The blocked command whose reason the record header says above its buttons (Ben, 4 Nov): the object's declared main
 * command (`main: true`, the record's goal: Release payment… on a purchase invoice, whose leading button follows its
 * state), else the command that leads the header, while it cannot run. Any other blocked command is a side action
 * (Stop trading…, Change payment method…) and is only folded under "Not available now".
 */
export function headerBlockedLeadOf(
  commands: readonly Command[],
  declaredMain: string | null,
): Command | null {
  const declared = commands.find((c) => c.name === declaredMain);
  if (declared && !declared.enabled) return declared;
  const lead = headerLeadOf(commands, declaredMain);
  return lead && !lead.enabled ? lead : null;
}

export function rowCommandsOf(
  commands: readonly Command[],
  declaredMain: string | null = null,
): RowCommands {
  if (
    !ownerHasSpoken(commands) &&
    commands.length <= ROW_BUTTONS_WITHOUT_MENU &&
    commands.every((c) => !c.destructive)
  )
    return { buttons: [...commands], more: [], unavailable: [] };
  const main = mainCommandOf(commands, declaredMain);
  return {
    buttons: main ? [main] : [],
    more: commands.filter((c) => c.enabled && c !== main),
    unavailable: commands.filter((c) => !c.enabled),
  };
}
